import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Send, MessageCircle, CheckCheck } from 'lucide-react';
import { chatService, type ChatMensaje, type ChatUsuario } from '../services/chat/chat.service';
import { webSocketService } from '../services/websocket/websocket.service';
import { useAuthStore } from '../store/auth.store';

export const ChatPage: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  
  // Fallback a localStorage si el store aún no cargó (ej: después de recargar página)
  const getStoredUserId = (): string | undefined => {
    if (user?.id) return user.id;
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed?.id;
      }
    } catch (e) {
      console.error('Error leyendo userData de localStorage:', e);
    }
    return undefined;
  };
  
  const currentUserId = getStoredUserId();
  const [usuarios, setUsuarios] = useState<ChatUsuario[]>([]);
  const [selectedUser, setSelectedUser] = useState<ChatUsuario | null>(null);
  const [mensajes, setMensajes] = useState<ChatMensaje[]>([]);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Cargar usuarios con info de chat (con o sin búsqueda)
  const loadUsuarios = useCallback(async (searchTerm?: string) => {
    setIsSearchingUsers(true);
    try {
      const data = await chatService.obtenerUsuariosParaChat(searchTerm);
      setUsuarios(data);
    } catch (error) {
      console.error('Error cargando usuarios para chat:', error);
    } finally {
      setIsSearchingUsers(false);
    }
  }, []);

  // Cargar lista inicial al montar
  useEffect(() => {
    loadUsuarios();
  }, [loadUsuarios]);

  // Búsqueda con debounce (server-side)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadUsuarios(search.trim() || undefined);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, loadUsuarios]);

  useEffect(() => {
    scrollToBottom();
  }, [mensajes]);

  // Reordenar usuarios al recibir mensaje nuevo
  const moverUsuarioAlTop = useCallback((usuarioId: string, ultimoMsg: any, incrementarNoLeidos: boolean) => {
    setUsuarios((prev) => {
      const idx = prev.findIndex((u) => u.id === usuarioId);
      if (idx === -1) return prev;

      const usuario = { ...prev[idx] };
      usuario.ultimoMensaje = ultimoMsg;
      if (incrementarNoLeidos) {
        usuario.noLeidos = (usuario.noLeidos || 0) + 1;
      }

      const nuevoArray = [...prev];
      nuevoArray.splice(idx, 1);
      nuevoArray.unshift(usuario);
      return nuevoArray;
    });
  }, []);

  // Escuchar mensajes de chat en tiempo real
  useEffect(() => {
    const handleChatMessage = (event: any) => {
      const mensaje: ChatMensaje = event;

      // Reordenar usuario remitente al top con preview
      const esParaMi = mensaje.destinatarioId === currentUserId;
      const esMio = mensaje.remitenteId === currentUserId;
      const otroId = esMio ? mensaje.destinatarioId : mensaje.remitenteId;

      moverUsuarioAlTop(otroId, {
        id: mensaje.id,
        remitenteId: mensaje.remitenteId,
        destinatarioId: mensaje.destinatarioId,
        contenido: mensaje.contenido,
        creadoEn: mensaje.creadoEn,
      }, esParaMi);

      // Si estoy viendo la conversación con el remitente, agregar mensaje
      if (selectedUser && (
        (mensaje.remitenteId === selectedUser.id && mensaje.destinatarioId === currentUserId) ||
        (mensaje.remitenteId === currentUserId && mensaje.destinatarioId === selectedUser.id)
      )) {
        setMensajes((prev) => {
          if (prev.some((m) => m.id === mensaje.id)) return prev;
          return [...prev, mensaje];
        });
        if (esParaMi) {
          chatService.marcarComoLeido(mensaje.remitenteId);
          // Limpiar noLeidos del usuario seleccionado
          setUsuarios((prev) => prev.map((u) => u.id === selectedUser.id ? { ...u, noLeidos: 0 } : u));
        }
      }
    };

    webSocketService.addChatMessageListener(handleChatMessage);
    return () => {
      webSocketService.removeChatMessageListener(handleChatMessage);
    };
  }, [selectedUser, currentUserId, moverUsuarioAlTop]);

  const loadConversacion = useCallback(async (otroUsuario: ChatUsuario) => {
    setIsLoading(true);
    try {
      const data = await chatService.obtenerConversacion(otroUsuario.id, 100);
      setMensajes(data);
      await chatService.marcarComoLeido(otroUsuario.id);
      // Limpiar noLeidos de este usuario en la lista
      setUsuarios((prev) => prev.map((u) => u.id === otroUsuario.id ? { ...u, noLeidos: 0 } : u));
    } catch (error) {
      console.error('Error cargando conversación:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSelectUser = (u: ChatUsuario) => {
    setSelectedUser(u);
    loadConversacion(u);
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedUser || isSending || !currentUserId) return;

    const contenido = input.trim();
    const tempId = `temp-${Date.now()}`;

    // Mensaje optimista para mostrar inmediatamente
    const mensajeOptimista: ChatMensaje = {
      id: tempId,
      remitenteId: currentUserId,
      destinatarioId: selectedUser.id,
      nombreRemitente: user?.nombreCompleto || 'Tú',
      contenido,
      leido: false,
      creadoEn: new Date().toISOString(),
    };

    // Agregar mensaje a la conversación inmediatamente
    setMensajes((prev) => [...prev, mensajeOptimista]);
    setInput('');
    setIsSending(true);

    // Reordenar usuario al top
    moverUsuarioAlTop(selectedUser.id, {
      id: tempId,
      remitenteId: currentUserId,
      destinatarioId: selectedUser.id,
      contenido,
      creadoEn: mensajeOptimista.creadoEn,
    }, false);

    try {
      const mensaje = await chatService.enviarMensaje(selectedUser.id, contenido);
      // Reemplazar mensaje optimista con el real del servidor
      setMensajes((prev) =>
        prev.map((m) => (m.id === tempId ? { ...mensaje, nombreRemitente: user?.nombreCompleto || mensaje.nombreRemitente } : m))
      );
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      // Marcar mensaje optimista como fallido
      setMensajes((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, contenido: `${m.contenido} ⚠️` } : m
        )
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Hoy';
    if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  };

  const formatHoraCorta = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  };

  // Los usuarios ya vienen filtrados del backend

  const getIniciales = (nombre: string) => {
    return nombre.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const totalNoLeidos = usuarios.reduce((sum, u) => sum + (u.noLeidos || 0), 0);

  return (
    <div className="h-full flex bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 7rem)' }}>
      {/* Sidebar */}
      <div className="w-80 border-r border-gray-200 flex flex-col bg-gray-50/50">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-medium text-gray-900 flex items-center space-x-2">
              <MessageCircle className="w-5 h-5 text-purple-600" />
              <span>Mensajes</span>
            </h2>
            {totalNoLeidos > 0 && (
              <span className="bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {totalNoLeidos}
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar usuario..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-purple-500/30 focus:bg-white transition-all outline-none"
            />
          </div>
        </div>

        {/* Lista de usuarios */}
        <div className="flex-1 overflow-y-auto">
          {isSearchingUsers ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
            </div>
          ) : usuarios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Search className="w-8 h-8 mb-2" />
              <p className="text-xs">
                {search.trim() ? 'No se encontraron usuarios' : 'No tienes conversaciones aún'}
              </p>
              {!search.trim() && (
                <p className="text-xs mt-1">Busca un usuario para iniciar un chat</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {usuarios.map((u) => {
                const isSelected = selectedUser?.id === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => handleSelectUser(u)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 transition-colors text-left ${
                      isSelected ? 'bg-purple-50 border-l-4 border-purple-500' : 'hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium">{getIniciales(u.nombreCompleto)}</span>
                      </div>
                      {u.noLeidos > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-medium rounded-full px-1">
                          {u.noLeidos}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-xs truncate ${u.noLeidos > 0 ? 'font-medium text-gray-900' : 'font-medium text-gray-700'}`}>
                          {u.nombreCompleto}
                        </p>
                        {u.ultimoMensaje && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                            {formatHoraCorta(u.ultimoMensaje.creadoEn)}
                          </span>
                        )}
                      </div>
                      <p className={`text-xs truncate ${u.noLeidos > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                        {u.ultimoMensaje ? (
                          u.ultimoMensaje.remitenteId === currentUserId
                            ? `Tú: ${u.ultimoMensaje.contenido}`
                            : u.ultimoMensaje.contenido
                        ) : (
                          u.email
                        )}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Panel de conversación */}
      <div className="flex-1 flex flex-col bg-white">
        {!selectedUser ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MessageCircle className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-xs font-medium text-gray-500">Selecciona un usuario</p>
            <p className="text-xs">Elige a alguien de la lista para iniciar una conversación</p>
          </div>
        ) : (
          <>
            {/* Header de conversación */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center space-x-3 bg-white">
              <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-medium">{getIniciales(selectedUser.nombreCompleto)}</span>
              </div>
              <div>
                <h3 className="text-xs font-medium text-gray-900">{selectedUser.nombreCompleto}</h3>
                <p className="text-xs text-gray-500">{selectedUser.tipoUsuario === 'administrador' ? 'Administrador' : 'Miembro'}</p>
              </div>
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-gray-50/30">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : mensajes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <MessageCircle className="w-10 h-10 mb-2" />
                  <p className="text-xs">No hay mensajes aún</p>
                  <p className="text-xs">Envía el primer mensaje a {selectedUser.nombreCompleto}</p>
                </div>
              ) : (
                (() => {
                  let lastDate = '';
                  return mensajes.map((msg) => {
                    const isMine = String(msg.remitenteId).trim() === String(currentUserId).trim();
                    const msgDate = formatDate(msg.creadoEn);
                    const showDate = msgDate !== lastDate;
                    lastDate = msgDate;

                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="flex justify-center my-3">
                            <span className="text-[10px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{msgDate}</span>
                          </div>
                        )}
                        <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`}>
                          <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-xs ${
                            isMine
                              ? 'bg-purple-600 text-white rounded-br-md'
                              : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'
                          }`}>
                            {!isMine && (
                              <p className={`text-xs font-medium mb-0.5 ${isMine ? 'text-purple-200' : 'text-purple-600'}`}>
                                {msg.nombreRemitente}
                              </p>
                            )}
                            <p className="leading-relaxed">{msg.contenido}</p>
                            <div className={`flex items-center justify-end space-x-1 mt-1 ${isMine ? 'text-purple-200' : 'text-gray-400'}`}>
                              <span className="text-[10px]">{formatTime(msg.creadoEn)}</span>
                              {isMine && (
                                <CheckCheck className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 bg-white border-t border-gray-200">
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Escribe a ${selectedUser.nombreCompleto}...`}
                  className="flex-1 px-4 py-2.5 text-xs bg-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-purple-500/30 focus:bg-white transition-all outline-none"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isSending}
                  className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSending ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
